import React from "react";
import AnimatedNumberAlts from "../home/animatedNumberAlts";

function fmt(n, d = 4) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(d) : "—";
}
function fmtDate(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleString();
}
function fmtPct(n, d = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(d)}%`;
}

function directionFromRecommendation(rec) {
  const r = String(rec || "").toUpperCase();
  if (r.includes("SELL")) return "SHORT";
  if (r.includes("BUY")) return "LONG";
  return "NEUTRAL";
}

function safeNum(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : null;
}

// Profit/loss % from entry to current, direction-aware
function pnlFromEntryPct({ dir, entry, current }) {
  const e = safeNum(entry);
  const c = safeNum(current);
  if (!e || e <= 0 || c == null) return null;

  // LONG: (c/e - 1) * 100
  // SHORT: (e/c - 1) * 100  (profit when price drops)
  if (dir === "SHORT") return ((e / c) - 1) * 100;
  return ((c / e) - 1) * 100;
}

// % distance remaining to TP (direction-aware).
// Positive => still needs to move that much (in favorable direction)
// Zero/negative => TP reached/passed
function distToTpPct({ dir, current, tp }) {
  const c = safeNum(current);
  const t = safeNum(tp);
  if (!c || c <= 0 || t == null) return null;

  // LONG: needs + from current to tp => (tp/current - 1) * 100
  // SHORT: needs - from current to tp => (current/tp - 1) * 100, but represent as positive if tp < current
  if (dir === "SHORT") return ((c / t) - 1) * 100;
  return ((t / c) - 1) * 100;
}


export default function AiTriggeredRecentWidget({
  limit = 50,
  refreshMs = 30_000,
  title = "AI Active Trades",
}) {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");

  const [sortField, setSortField] = React.useState("timestamp");
  const [sortOrder, setSortOrder] = React.useState("desc");

  const PAGE_SIZE = 20;
  const [page, setPage] = React.useState(1);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch(`${baseUrl}/api/aitriggered/recent?limit=${encodeURIComponent(limit)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, limit]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    const id = setInterval(() => { if (!cancelled) load(); }, refreshMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [load, refreshMs]);

  function handleHeaderClick(field) {
    setSortField((prev) => {
      if (prev === field) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortOrder("asc");
      return field;
    });
  }

  const sortedItems = React.useMemo(() => {
    const arr = [...(items || [])];
    const dir = sortOrder === "asc" ? 1 : -1;

    const getVal = (it) => {
      const a = it?.analysis || {};
      const recDir = directionFromRecommendation(a.recommendation);
      const pos = recDir === "LONG" ? it?.longPosition : recDir === "SHORT" ? it?.shortPosition : null;
      

      switch (sortField) {
        case "token": return String(it.token || "");
        case "timestamp": return new Date(it.timestamp || 0).getTime();
        case "priceAtAnalysis": return Number(it.priceAtAnalysis || 0);
        case "currentPrice": return Number(it.currentPrice || 0);
        case "gain": return Number(it.gain || 0);
        case "recommendation": return String(a.recommendation || "");
        case "tradeType": return String(a.tradeType || "");
        case "score": return Number(a.score || 0);
        case "successRatio": return Number(a.successRatio || 0);
        case "entry": return Number(pos?.entry || 0);
        case "tp": return Number(pos?.tp || 0);
        case "sl": return Number(pos?.sl || 0);
        case "pnlNow": {
            const e = pos?.entry;
            const c = it.currentPrice;
            const p = pnlFromEntryPct({ dir: recDir, entry: e, current: c });
            return Number.isFinite(p) ? p : -Infinity;
            }
            case "toTp": {
            const c = it.currentPrice;
            const t = pos?.tp;
            const d = distToTpPct({ dir: recDir, current: c, tp: t });
            return Number.isFinite(d) ? d : Infinity;
            }
        default: return 0;
      }
    };

    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "string" || typeof vb === "string") {
        return dir * String(va).localeCompare(String(vb));
      }
      return dir * (Number(va) - Number(vb));
    });

    return arr;
  }, [items, sortField, sortOrder]);

  const totalPages = React.useMemo(
    () => Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE)),
    [sortedItems.length]
  );

  const pageItems = React.useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, page]);

  React.useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  function openDrawerById(id) {
    setSelectedId(id);
    setDrawerOpen(true);
  }

  function pillClass(rec) {
    const d = directionFromRecommendation(rec);
    if (d === "LONG") return "pill pill-green";
    if (d === "SHORT") return "pill pill-red";
    return "pill pill-yellow";
  }

  return (
    <div className="hrw-wrap">
      <div className="hrw-actions" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>

        <h2 className="mobile-h2">{title}</h2>

        <div style={{ width: 110 }} />
      </div>

      {err && <div className="hrw-error">{err}</div>}

      <div className="moonshot-table-container">
        <table className="moonshot-table">
          <thead>
            <tr>
              <th onClick={() => handleHeaderClick("token")}>
                Token {sortField === "token" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => handleHeaderClick("pnlNow")}>
                PnL / To TP {sortField === "pnlNow" && (sortOrder === "asc" ? "↓" : "↑")}
                </th>
              <th onClick={() => handleHeaderClick("timestamp")} style={{display:"none"}}>
                Timestamp {sortField === "timestamp" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => handleHeaderClick("priceAtAnalysis")}>
                Price@Analysis {sortField === "priceAtAnalysis" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>

              <th onClick={() => handleHeaderClick("gain")}>
                Set Gain {sortField === "gain" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => handleHeaderClick("recommendation")}>
                Rec {sortField === "recommendation" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>

              <th onClick={() => handleHeaderClick("tradeType")} style={{display:"none"}}>
                Type {sortField === "tradeType" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>

              <th onClick={() => handleHeaderClick("score")}>
                Score {sortField === "score" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>

              <th onClick={() => handleHeaderClick("currentPrice")}>
                Current {sortField === "currentPrice" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => handleHeaderClick("entry")}>
                Entry {sortField === "entry" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
                <th onClick={() => handleHeaderClick("tp")}>
                TP {sortField === "tp" && (sortOrder === "asc" ? "↓" : "↑")}
                </th>
              <th onClick={() => handleHeaderClick("sl")}>
                SL {sortField === "sl" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => handleHeaderClick("successRatio")}>
                Success {sortField === "successRatio" && (sortOrder === "asc" ? "↓" : "↑")}
              </th>
            </tr>
          </thead>

          <tbody>
            {pageItems.map((it, idx) => {
              const a = it.analysis || {};
              const dir = directionFromRecommendation(a.recommendation);
              const pos = dir === "LONG" ? (it.longPosition || {}) : dir === "SHORT" ? (it.shortPosition || {}) : {};
              const entry = pos.entry;
            const tp = pos.tp;
            const current = it.currentPrice;

            const pnlNow = pnlFromEntryPct({ dir, entry, current });
            const toTp = distToTpPct({ dir, current, tp });

            // “how far away from projected gain” (two good options):
            // Option 1 (recommended): distance to TP (actionable)
            // Option 2: remaining % to projected gain: projectedGain - pnlNow (less accurate if gain != TP-based)
            const projectedGain = safeNum(it.gain); // your stored projected gain %
            const remainingToProjected = (projectedGain != null && pnlNow != null) ? (projectedGain - pnlNow) : null;

            const pnlClass = Number.isFinite(pnlNow) ? (pnlNow > 0 ? "pos" : pnlNow < 0 ? "neg" : "") : "";


              return (
                <tr
                  key={it._id || idx}
                  className="clickable-row"
                  onClick={() => openDrawerById(it._id)}
                >
                  <td style={{ fontWeight: 700 }}>{String(it.token || "—").replace(/USDT$/i, "")}</td>
                                    <td>
                    <div className={pnlClass} style={{ fontWeight: 700, fontSize: 10 }}>
                        {Number.isFinite(pnlNow) ? (
                        <><AnimatedNumberAlts value={pnlNow} decimals={2} />%</>
                        ) : "—"}
                    </div>

                    <div style={{ opacity: 0.8, fontSize: 10 }}>
                        {Number.isFinite(toTp) ? (
                        toTp <= 0
                            ? "TP hit"
                            : <><AnimatedNumberAlts value={toTp} decimals={2} />% to TP</>
                        ) : "—"}
                        {/* OR show remainingToProjected instead of toTp */}
                        {/* {Number.isFinite(remainingToProjected) ? ` • ${remainingToProjected.toFixed(2)}% to proj` : ""} */}
                    </div>
                    </td>

                  <td style={{display:"none"}}>{fmtDate(it.timestamp)}</td>
                  <td>
                    {Number.isFinite(Number(it.priceAtAnalysis)) ? (
                      <>$<AnimatedNumberAlts value={Number(it.priceAtAnalysis)} decimals={6} /></>
                    ) : "—"}
                  </td>



                  <td className={Number(it.gain) > 0 ? "pos" : Number(it.gain) < 0 ? "neg" : ""}>
                    {Number.isFinite(Number(it.gain)) ? (
                      <><AnimatedNumberAlts value={Number(it.gain)} decimals={2} />%</>
                    ) : "—"}
                  </td>

                  <td>
                    <span className={pillClass(a.recommendation)} style={{ fontSize: 8 }}>
                      {a.recommendation || "—"}
                    </span>
                  </td>

                  <td style={{display:"none"}}>{a.tradeType || "—"}</td>
                  <td>{fmt(a.score, 2)}</td>

                  <td>
                    {Number.isFinite(Number(it.currentPrice)) ? (
                      <>$<AnimatedNumberAlts value={Number(it.currentPrice)} decimals={6} /></>
                    ) : "—"}
                  </td>

                  <td>${Number.isFinite(Number(pos.entry)) ? fmt(pos.entry, 6) : "—"}</td>
                  <td>${Number.isFinite(Number(pos.tp)) ? fmt(pos.tp, 6) : "—"}</td>
                  <td>${Number.isFinite(Number(pos.sl)) ? fmt(pos.sl, 6) : "—"}</td>

                  <td>{Number.isFinite(Number(a.successRatio)) ? fmtPct(a.successRatio, 2) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>&lt;</button>
        <span>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>&gt;</button>
      </div>

    </div>
  );
}
